/**
 * chunkReload.js
 *
 * Auto-recarga UNA SOLA VEZ cuando un "chunk" lazy no se puede cargar
 * (ChunkLoadError / "Loading chunk N failed"). Causa típica: el usuario tiene
 * una pestaña vieja abierta y, tras un deploy de frontend, los hashes de los
 * chunks han cambiado → el chunk que pide ya no existe. Una recarga trae el
 * index.html nuevo + los chunks nuevos.
 *
 * Aprobado por el Guardián (2026-06-30). Condición: recargar UNA vez, con
 * guarda en sessionStorage para NO entrar en bucle si el fallo persiste por
 * otra causa (p.ej. red caída, chunk realmente roto).
 *
 * SOLO FRONTEND · no toca BD ni backend.
 */

const KEY = 'chunkReload:lastTs';
// Si ya recargamos hace menos de esta ventana, NO volvemos a recargar (anti-bucle).
// Pasada la ventana, un fallo nuevo (otro deploy posterior) sí podrá recargar.
const VENTANA_MS = 30000;

export function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || '';
  const msg = (error.message || String(error) || '');
  if (name === 'ChunkLoadError') return true;
  // CRA/webpack: "Loading chunk 6195 failed." · también CSS chunks.
  return /Loading chunk [\w-]+ failed/i.test(msg)
    || /Loading CSS chunk [\w-]+ failed/i.test(msg)
    || /import\(\) .*chunk/i.test(msg);
}

/**
 * Si `error` es un fallo de carga de chunk y no hemos recargado hace poco,
 * recarga la página UNA vez. Devuelve true si ha disparado la recarga.
 */
export function reloadOnceForChunkError(error) {
  try {
    if (!isChunkLoadError(error)) return false;
    if (typeof window === 'undefined') return false;
    const last = Number(window.sessionStorage.getItem(KEY) || 0);
    const now = Date.now();
    if (now - last < VENTANA_MS) {
      // Ya recargamos hace poco y sigue fallando → NO insistir (evita bucle).
      return false;
    }
    window.sessionStorage.setItem(KEY, String(now));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

/**
 * Red de seguridad global: cubre fallos de chunk que NO caen en un
 * ErrorBoundary de React (p.ej. promesas import() rechazadas sin capturar).
 */
export function installGlobalChunkReload() {
  if (typeof window === 'undefined') return;
  if (window.__chunkReloadInstalled) return;
  window.__chunkReloadInstalled = true;
  window.addEventListener('unhandledrejection', (ev) => {
    reloadOnceForChunkError(ev && ev.reason);
  });
  window.addEventListener('error', (ev) => {
    reloadOnceForChunkError((ev && ev.error) || ev);
  });
}
